import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  factorCode: '',
  displayName: '',
  scopeMode: 'UNIVERSAL',
  bodyPartTemplateId: '',
  displayOrder: 0,
  active: true,
  adminNotes: '',
};

function normalizeTemplateToForm(template) {
  return {
    factorCode: template?.factorCode || '',
    displayName: template?.displayName || '',
    scopeMode: template?.bodyPartTemplateId ? 'BODY_PART' : 'UNIVERSAL',
    bodyPartTemplateId: template?.bodyPartTemplateId
      ? String(template.bodyPartTemplateId)
      : '',
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
    adminNotes: template?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

export default function AggravatingFactorTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.aggravatingFactorTemplateId);

  const bodyPartMap = useMemo(() => {
    const map = new Map();
    bodyParts.forEach((item) => map.set(Number(item.bodyPartTemplateId), item));
    return map;
  }, [bodyParts]);

  const visibleTemplates = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return [...templates]
      .filter((item) => {
        if (scopeFilter === 'UNIVERSAL' && item.bodyPartTemplateId) {
          return false;
        }
        if (scopeFilter === 'BODY_PART' && !item.bodyPartTemplateId) {
          return false;
        }
        if (!search) {
          return true;
        }
        return [item.factorCode, item.displayName, item.adminNotes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(search);
      })
      .sort((a, b) => {
        const orderDiff = Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0);
        return orderDiff || String(a.displayName || '').localeCompare(String(b.displayName || ''));
      });
  }, [templates, searchTerm, scopeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setActionError('');
      const [templateData, bodyPartData] = await Promise.all([
        clinicalTemplateAdminService.getAllAggravatingFactorTemplates(),
        clinicalTemplateAdminService.getAllBodyPartTemplates(),
      ]);
      setTemplates(Array.isArray(templateData) ? templateData : []);
      setBodyParts(Array.isArray(bodyPartData) ? bodyPartData : []);
    } catch (error) {
      setActionError(error?.message || 'Aggravating-factor configuration could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateFormField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({ ...EMPTY_FORM });
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateForm = () => {
    const factorCode = String(form.factorCode || '').trim();
    const displayName = String(form.displayName || '').trim();

    if (!factorCode) {
      return 'Aggravating-factor code is required.';
    }
    if (!/^[A-Za-z0-9_-]+$/.test(factorCode)) {
      return 'Code may contain only letters, numbers, underscores, and hyphens.';
    }
    if (!displayName) {
      return 'Display name is required.';
    }
    if (form.scopeMode === 'BODY_PART') {
      const id = Number(form.bodyPartTemplateId);
      if (!Number.isInteger(id) || id <= 0) {
        return 'Select a body part for the body-part-specific option.';
      }
    }
    const displayOrder = Number(form.displayOrder);
    if (!Number.isInteger(displayOrder) || displayOrder < 0) {
      return 'Display order must be a whole number of zero or greater.';
    }
    if (String(form.adminNotes || '').trim().length > 500) {
      return 'Admin notes cannot exceed 500 characters.';
    }
    return '';
  };

  const buildRequest = () => ({
    factorCode: String(form.factorCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    bodyPartTemplateId:
      form.scopeMode === 'BODY_PART' ? Number(form.bodyPartTemplateId) : null,
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
    adminNotes: String(form.adminNotes || '').trim() || null,
  });

  const submitForm = async (event) => {
    event.preventDefault();
    const errorMessage = validateForm();
    if (errorMessage) {
      setActionError(errorMessage);
      return;
    }

    try {
      setSaving(true);
      setActionError('');
      const request = buildRequest();

      if (isEditing) {
        await clinicalTemplateAdminService.updateAggravatingFactorTemplate(
          editingTemplate.aggravatingFactorTemplateId,
          request
        );
        setSuccess('Aggravating-factor template updated.');
      } else {
        await clinicalTemplateAdminService.createAggravatingFactorTemplate(request);
        setSuccess('Aggravating-factor template created.');
      }

      resetForm();
      await loadData();
      window.setTimeout(() => setSuccess(''), 3500);
    } catch (error) {
      setActionError(error?.message || 'Aggravating-factor template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    try {
      setActionError('');
      await clinicalTemplateAdminService.setAggravatingFactorTemplateActive(
        template.aggravatingFactorTemplateId,
        !template.active
      );
      await loadData();
    } catch (error) {
      setActionError(error?.message || 'Active status could not be updated.');
    }
  };

  return (
    <div className="aggravating-factor-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">EVALUATION HISTORY</span>
            <h2>{isEditing ? 'Edit Aggravating Factor' : 'Create Aggravating Factor'}</h2>
            <p>
              Universal factors are available for every body part. Body-part-specific
              factors are added only when that body part is being documented.
            </p>
          </div>
          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          )}
        </div>

        {actionError && <div className="clinical-template-message error" role="alert">{actionError}</div>}
        {success && <div className="clinical-template-message success" role="status">{success}</div>}

        <form onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Factor Code
              <input
                type="text"
                value={form.factorCode}
                onChange={(event) => updateFormField('factorCode', event.target.value.toUpperCase())}
                maxLength={120}
                disabled={isEditing || saving}
                placeholder="PROLONGED_STANDING"
              />
              {isEditing && <small>Code cannot be changed after creation.</small>}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) => updateFormField('displayName', event.target.value)}
                maxLength={150}
                disabled={saving}
                placeholder="Prolonged Standing"
              />
            </label>

            <label>
              Availability
              <select
                value={form.scopeMode}
                onChange={(event) => {
                  const value = event.target.value;
                  setForm((current) => ({
                    ...current,
                    scopeMode: value,
                    bodyPartTemplateId: value === 'UNIVERSAL' ? '' : current.bodyPartTemplateId,
                  }));
                }}
                disabled={saving}
              >
                <option value="UNIVERSAL">Universal — all body parts</option>
                <option value="BODY_PART">One body part only</option>
              </select>
            </label>

            {form.scopeMode === 'BODY_PART' && (
              <label>
                Body Part
                <select
                  value={form.bodyPartTemplateId}
                  onChange={(event) => updateFormField('bodyPartTemplateId', event.target.value)}
                  disabled={saving}
                >
                  <option value="">Select body part</option>
                  {bodyParts.map((item) => (
                    <option key={item.bodyPartTemplateId} value={item.bodyPartTemplateId}>
                      {item.displayName} ({item.bodyPartCode}){!item.active ? ' - Inactive' : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) => updateFormField('displayOrder', event.target.value)}
                disabled={saving}
              />
            </label>
          </div>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => updateFormField('active', event.target.checked)}
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>Available in new IE/Re-Evaluation aggravating-factor pickers.</small>
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              value={form.adminNotes}
              onChange={(event) => updateFormField('adminNotes', event.target.value)}
              maxLength={500}
              disabled={saving}
              placeholder="Optional configuration notes..."
            />
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Save Aggravating Factor' : 'Create Aggravating Factor'}
            </button>
            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">CONFIGURED OPTIONS</span>
            <h2>Aggravating Factors</h2>
            <p>{templates.length} configured reusable options.</p>
          </div>
          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadData}
            disabled={loading || saving}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="clinical-template-list-toolbar" style={{ gridTemplateColumns: '1fr 220px' }}>
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search code, name, notes..."
          />
          <select value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value)}>
            <option value="ALL">All availability</option>
            <option value="UNIVERSAL">Universal only</option>
            <option value="BODY_PART">Body-part specific</option>
          </select>
        </div>

        {loading ? (
          <div className="clinical-template-empty-state">Loading aggravating factors...</div>
        ) : visibleTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">No aggravating factors match the current filter.</div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Factor</th>
                  <th>Availability</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleTemplates.map((template) => {
                  const bodyPart = bodyPartMap.get(Number(template.bodyPartTemplateId));
                  return (
                    <tr key={template.aggravatingFactorTemplateId}>
                      <td>{template.displayOrder}</td>
                      <td>
                        <strong>{template.displayName}</strong>
                        <small className="clinical-template-table-subtext">{template.factorCode}</small>
                        {template.adminNotes && (
                          <small className="clinical-template-table-subtext">{template.adminNotes}</small>
                        )}
                      </td>
                      <td>
                        {template.bodyPartTemplateId
                          ? bodyPart?.displayName || `Body Part ${template.bodyPartTemplateId}`
                          : 'Universal'}
                      </td>
                      <td>
                        <span className={`clinical-template-status ${template.active ? 'active' : 'inactive'}`}>
                          {template.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {formatDateTime(template.updatedAt)}
                        <small className="clinical-template-table-subtext">
                          Staff {template.updatedByStaffId ?? 'N/A'}
                        </small>
                      </td>
                      <td>
                        <div className="clinical-template-row-actions">
                          <button
                            type="button"
                            className="clinical-template-secondary-button"
                            onClick={() => beginEdit(template)}
                            disabled={saving}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={template.active ? 'clinical-template-danger-button' : 'clinical-template-success-button'}
                            onClick={() => toggleActive(template)}
                            disabled={saving}
                          >
                            {template.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
